import { useMemo, useState } from 'react';
import JIRAIcon from '@root/assets/svg/Kubernetes/JIRAIcon';
import SideOverlayTabMenu, { SideOverlayBody } from '@root/components/v2/SideOverlayTabMenu';
import CircularCloseButton from '@root/components/v2/ui-elements/CircularCloseButton';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { Button } from '@root/components/v3/button';
import { Sheet, SheetContent } from '@root/components/v3/sheet';
import K8sOverlayHeaderIdentity from '../../Sideoverlay/K8sOverlayHeaderIdentity';
import CveDetailsTab from './CveDetailsTab';
import { buildFindingDetails } from './mockData';
import OverviewTab from './OverviewTab';
import { FINDING_DETAILS_MENU_CATEGORIES } from './tabMenu';
import './style.scss';

const TABS = { overview: OverviewTab, cve: CveDetailsTab };

// Side overlay for a row of the Vulnerabilities > Findings table — same shell as the AI Stories
// overlay (Sheet + header + vertical tab menu + scrollable body).
const FindingDetailsOverlay = ({ open, onClose, selectedRow }) => {
  const [currentTab, setCurrentTab] = useState('overview');
  // TEMP: mock details, seeded from the clicked row (see ./mockData).
  const details = useMemo(() => buildFindingDetails(selectedRow ?? {}), [selectedRow]);

  if (!selectedRow) return null;

  const Tab = TABS[currentTab];

  return (
    <Sheet open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <SheetContent
        side="right"
        // Pinned 55px from the top (see sheetVariants), so a full 100dvh would push its last 55px below the
        // viewport; 85% wide like every other side overlay.
        className="FindingDetails flex flex-col w-[85%] !max-w-[100%] h-[calc(100dvh-55px)] p-0 gap-0"
        // The Sheet paints a fixed white; the overlay's own token follows the theme.
        style={{ backgroundColor: 'var(--finding-details-bg)' }}
        hideClose={true}>
        <header className="FindingDetails__Header">
          {/* Same header as the CVEs overlay, titled by the finding's workload with the Findings table's
              Workload icon; the CVE (with its severity), cluster and environment sit beneath. */}
          <K8sOverlayHeaderIdentity
            type="findings"
            selectedRow={selectedRow}
            title={selectedRow.workload_name ?? details.cveId}
          />

          <div className="FindingDetails__Actions">
            {/* TEMP: ticketing is wired up together with the Findings details API. */}
            <Tooltip title="Jira tickets become available once the Findings API is connected" isIcon={true}>
              <span>
                <Button variant="outline" className="h-8 gap-1 text-[12px]" disabled>
                  <JIRAIcon />
                  Jira ticket
                </Button>
              </span>
            </Tooltip>
            <CircularCloseButton onClick={onClose} />
          </div>
        </header>

        <SideOverlayBody
          menu={
            <SideOverlayTabMenu
              categories={FINDING_DETAILS_MENU_CATEGORIES}
              activeTab={currentTab}
              onChange={setCurrentTab}
              ariaLabel="Finding sections"
            />
          }>
          {/* The only scroll container; end-of-scroll spacing lives on the tab content. */}
          <div className="FindingDetails__Body CustomScrollbar">
            <Tab details={details} selectedRow={selectedRow} />
          </div>
        </SideOverlayBody>
      </SheetContent>
    </Sheet>
  );
};

export default FindingDetailsOverlay;
